import {uuid} from 'uuidv4';
import {getEffect} from 'effects/EffectsList.js';
import {SAMPLER, INSTRUMENT, MIDI, AUDIO} from 'constants/track-types.js';
import {getVolume} from 'utils/getVolume.js';
import GroupVolumeChangeEvent from 'session/GroupVolumeChangeEvent.js';
import GroupMuteEvent from 'session/GroupMuteEvent.js';
import {setGroupVolume,
        setGroupMuted,
        setGroupPanning} from 'actions/index.js';
import ParameterContext from 'effects/ParameterContext.js';
import * as Tone from 'tone';
import {
    setEffectParameterValues,
    setGroupTracks,
} from 'actions/index.js';
import EffectsChain from 'effects/EffectsChain.js';


const MUTED_VOLUME = -125;
const UNMUTED_VOLUME = 0;

class Group {
    constructor(tags, channelMixer, groupNumber, sequencerEngine, subSequence) {
        this.id = uuid();
        this.tracks = [];
        this.subSequence = subSequence;
        this.sequencerEngine = sequencerEngine;
        this.groupNumber = groupNumber;
        this.tags = tags;
        this.inputNode = new Tone.Volume();
        this.outputNode = new Tone.PanVol();

        // level meter for the group
        this.muteableNode = new Tone.Volume();
        this.muteableNode.connect(this.outputNode);

        this.effectsChain = new EffectsChain(
            this.inputNode, this.muteableNode, 
            new ParameterContext().forGroup(this),
            sequencerEngine);

        this.effectsChain.setupEffects();

        // 70/127
        this.setVolume(-2);

        this.channelMixer = channelMixer;
        this.outputs = undefined;

        this.muted = false;

        this.panning = 0;
    }

    updateRedux() {
        this.sequencerEngine.store.dispatch(
            setGroupTracks(this.id, [... this.tracks]));
    }

    get mix() {
        return this.subSequence.mix;
    }

    set solo(s) {
        this.tracks.forEach(t => t.solo(s));
    }

    get solo() {
        let soloed = false;
        for (let i in this.tracks) {
            soloed = soloed || this.tracks[i].getSolo();
        }
        return soloed;
    }

    get effects() {
        return this.effectsChain.effects;
    }

    getEffectNumber(effect, isMidiEffect) {
        return this.effects.indexOf(effect);
    }

    dispose() {
        this.outputs = this.channelMixer.disconnect(this.outputNode, this.outputs);

        this.inputNode.dispose();
        this.muteableNode.dispose();
        this.outputNode.dispose();

        this.inputNode = undefined;
        this.outputNode = undefined;

        this.effectsChain.dispose();
        this.effectsChain = undefined;
    }

    mute(muted=true) {
        if (this.sequencerEngine.store.getState().mix.isRecordingMix) {
            this.mix.addEvent(new GroupMuteEvent(this.groupNumber, muted));
        }
        this.muted = muted;
        this.muteableNode.volume.value = muted ? MUTED_VOLUME : UNMUTED_VOLUME;
        // this.sequencerEngine.store.dispatch(setGroupMuted(this.groupNumber, muted));
    }

    moveEffectTo(effectFrom, effectTo) {
        this.effectsChain.moveEffectTo(effectFrom, effectTo);
    }

    deleteEffect(effect) {
        this.effectsChain.deleteEffect(effect);
    }

    connectAudio(maxChannel) {
        this.outputs = this.channelMixer.connect(this.outputNode);
    }

    async fromJson(json, sequencerEngine) {
        if (json.newVolume) {
            this.setVolume(getVolume(json.volume, 0, 128));
        } else if (json.newestVolume) {
            this.setVolume(json.volume);
        }else {
            let volume = 128.0 * (json.volume + 40.0) / 43.0;
            this.setVolume(volume);
        }
        let promises:Promise<void>[] = [];
        for (let i=0; i < json.tracks.length; i++) {
            let track = sequencerEngine.tracks[json.tracks[i]];
            if (track !== undefined) {
                if (track.getTrackType() === SAMPLER &&
                    track.sample) {
                    promises.push(track.sample.fetchTags());
                }
                this.addTrack(track);
            }
        }

        let bulk = {};
        for (var j=0; j < json.effects.length; j++) {
            let ret = await getEffect(json.effects[j], sequencerEngine.tracks, sequencerEngine);
            this.effectsChain.addEffect(ret.effect);
            bulk = {
                ... bulk, ... ret.bulk
            };
        }

        if (Object.keys(bulk).length > 0) {
            this.sequencerEngine.store.dispatch(
                setEffectParameterValues(bulk));
        }

        let resolvedTags = await Promise.all(promises);
        let tagsSet = new Set();
        for (let i=0; i < resolvedTags.length; i++) {
            resolvedTags[i].forEach(tag => tagsSet.add(tag));

        }

        this.tags = Array.from(tagsSet);

        return this;
    }

    getSendNodes() {
        return [];
    }

    addTrack(track) {
        if (this.hasTrack(track)) {
            return;
        }
        this.tracks.push(track);
        track.addToGroup(this);
    }

    hasTrack(track) {
        return this.tracks.indexOf(track) > -1;
    }

    setVolume(volume) {
        volume = Math.min(0, volume);
        if (this.sequencerEngine.store.getState().mix.isRecordingMix) {
            this.mix.addEvent(
                new GroupVolumeChangeEvent(this.groupNumber, volume));
        }
        this.volume = volume;
        this.outputNode.volume.linearRampToValueAtTime(volume, Tone.Master.context.currentTime + 0.01);
        this.sequencerEngine.store.dispatch(setGroupVolume(this.groupNumber, volume));
    }

    setPanning(panning) {
        this.panning = panning;
        this.outputNode.pan.value = panning;
        // this.sequencerEngine.store.dispatch(setGroupPanning(this.groupNumber, panning));
    }

    updateEffects(effects) {
        this.effectsChain.updateEffects();
    }

    getJson(sequencerEngine) {
        // given the sequencerEngine we know what tracks are what index
        // so we do that
        let trackNumbers = this.tracks.map(
            track => track.trackNumber);

        return {
            newestVolume: true,
            tracks: trackNumbers,
            effects: this.effectsChain.getJson(),
            volume: this.volume,
            outputs: this.outputs,
        };
    }

}

export default Group;
